import { useContext, useEffect, useState, useRef } from 'react';
import { FieldArray, Form, Formik, FormikProps, validateYupSchema, yupToFormErrors } from 'formik';
import cn from 'classnames';
import parse from 'html-react-parser';

import cms from '../../../data/cms.json';
import config from '../../../config.json';
import styles from './Step2.module.scss';
import { validationSchema } from './validations';
import { scrollToError } from '../../../utils/scrollToError';
import { formatYears } from '../../../utils/formatYears';
import { TextField } from '../../TextField/TextField';
import { SelectField } from '../../SelectField/SelectField';
import { ToggleField } from '../../ToggleField/ToggleField';
import { triggerGtmEvent } from '../../../utils/triggerGtmEvent';
import { Title } from '../../Title/Title';
import { Grid } from '../../Grid/Grid';
import { Button } from '../../Button/Button';
import { Label } from '../../Label/Label';
import { AppContext } from '../../../context/AppContext';
import { AmountButton } from '../../AmountButton/AmountButton';
import { ValuesProps } from '../Step6/Step6';
import { FormItemError } from '../../FormItemError/FormItemError';
import { unlockScroll } from '../../../utils/bodyScrollLock';
import ToggleListWrapper from '../../ToggleListWrapper/ToggleListWrapper';

interface MortgagePopupProps {
    initialValues: any;
    setValues: (values: React.SetStateAction<any>, shouldValidate?: boolean) => void;
}

const MortgagePopup = ({ initialValues, setValues }: MortgagePopupProps) => {
    const ctx = useContext(AppContext);
    const [, setModalActive] = useState(true);
    const [submittedPopup, setSubmittedPopup] = useState(false);
    const [forceValidation, setForceValidation] = useState(false);

    const formikRef = useRef<FormikProps<typeof initialValues> | null>(null);

    useEffect(() => {
        if (forceValidation && formikRef.current) {
            formikRef.current.validateForm();
            setForceValidation(false);
        }
    }, [forceValidation]);

    return (
        <div className={styles.popup} data-test="mortgagePopup">
            <Title tag="h2" className="mb-7" data-test="mortgagePopupTitle">
                {cms.step2a.title}
            </Title>

            {cms.step2a.text && (
                <div className={cn(styles.text, 'mb-7')} data-test="mortgagePopupText">
                    {parse(cms.step2a.text)}
                </div>
            )}
            <div className={styles.content}>
                {initialValues && (
                    <Formik
                        innerRef={formikRef}
                        initialValues={initialValues}
                        validateOnBlur={false}
                        validateOnChange={submittedPopup}
                        validateOnMount={false}
                        validationSchema={validationSchema}
                        enableReinitialize
                        validate={(values) => {
                            setSubmittedPopup(true);
                            try {
                                validateYupSchema(values, validationSchema, true, values);
                            } catch (err) {
                                scrollToError();
                                return yupToFormErrors(err);
                            }
                            return {};
                        }}
                        onSubmit={(values) => {
                            setValues(values);
                            unlockScroll();
                            ctx.setPopupVisible(false);
                            setModalActive(false);
                        }}
                    >
                        {({ values, errors, handleSubmit }: ValuesProps) => (
                            <Form className={styles.form} data-test="form">
                                <div className="w-100">
                                    <Label htmlFor="hasChildren" data-test="hasChildrenLabel">
                                        Máte děti?
                                    </Label>
                                    <Grid cols="auto">
                                        <ToggleListWrapper>
                                            <div className="toggle-list">
                                                <ToggleField
                                                    name="hasChildren"
                                                    id="hasChildren-yes"
                                                    variant="radio"
                                                    value={config.QUESTIONNAIRE.ANSWER_YES}
                                                    error={!!errors.hasChildren}
                                                    data-test="hasChildren[Yes]"
                                                />
                                                <ToggleField
                                                    name="hasChildren"
                                                    id="hasChildren-no"
                                                    variant="radio"
                                                    value={config.QUESTIONNAIRE.ANSWER_NO}
                                                    error={!!errors.hasChildren}
                                                    data-test="hasChildren[No]"
                                                />
                                            </div>
                                            {errors.hasChildren && (
                                                <FormItemError text={errors.hasChildren} data-test="hasChildrenError" />
                                            )}
                                        </ToggleListWrapper>
                                    </Grid>
                                </div>

                                {values['hasChildren'] === config.QUESTIONNAIRE.ANSWER_YES && (
                                    <>
                                        <div className="w-100">
                                            <Label
                                                htmlFor="childrenAge"
                                                children={'<b>Věk vašich dětí</b>'}
                                                description={`Vyplňte pouze děti mladší než ${
                                                    config.QUESTIONNAIRE.CHILD_AGE.MAX + 1
                                                } let`}
                                                data-test="childrenAgeLabel"
                                            />

                                            <Grid cols="auto">
                                                <FieldArray
                                                    name="childrenAge"
                                                    render={(arrayHelpers) => (
                                                        <div className={styles.childrenInputsGroup}>
                                                            {(values['childrenAge'] || []).map(
                                                                (item: string, index: number) => {
                                                                    const isLast =
                                                                        index ===
                                                                        (values['childrenAge'] || []).length - 1;
                                                                    return (
                                                                        <div
                                                                            key={index}
                                                                            className={cn(
                                                                                styles.childrenInputRow,
                                                                                'formFieldWrapper'
                                                                            )}
                                                                            data-test={`childrenAge[${index}]`}
                                                                        >
                                                                            {/* <TextField
                                                                            key={`childrenAge[${index}]`}
                                                                            name={`childrenAge[${index}]`}
                                                                            variant="number"
                                                                            suffix={formatYears(item)}
                                                                            className="field-small"
                                                                            max={config.QUESTIONNAIRE.CHILD_AGE.MAX}
                                                                            error={errors?.childrenAge?.[index] ?? ''}
                                                                        /> */}

                                                                            <div className={styles.childrenInput}>
                                                                                <SelectField
                                                                                    name={`childrenAge[${index}]`}
                                                                                    options={Array.from(
                                                                                        { length: 18 },
                                                                                        (_, i) => {
                                                                                            const v = String(i + 1);
                                                                                            return {
                                                                                                value: v,
                                                                                                label: `${v} ${formatYears(
                                                                                                    String(v)
                                                                                                )}`,
                                                                                            };
                                                                                        }
                                                                                    )}
                                                                                    className="select-small"
                                                                                    placeholder=""
                                                                                    error={
                                                                                        errors?.childrenAge?.[index] ??
                                                                                        ''
                                                                                    }
                                                                                    allowFieldValidation
                                                                                    data-test={`childrenAge[${index}]Select`}
                                                                                />

                                                                                {errors?.childrenAge?.[index] && (
                                                                                    <FormItemError
                                                                                        text={errors.childrenAge[index]}
                                                                                        data-test={`childrenAge[${index}]Error`}
                                                                                    />
                                                                                )}
                                                                            </div>

                                                                            {isLast && (
                                                                                <>
                                                                                    {values['childrenAge'].length <
                                                                                        config.QUESTIONNAIRE
                                                                                            .MAX_CHILDS && (
                                                                                        <AmountButton
                                                                                            text={
                                                                                                cms.common
                                                                                                    .addChildButton
                                                                                            }
                                                                                            onClick={() => {
                                                                                                arrayHelpers.push('');
                                                                                            }}
                                                                                            data-test={`addChildButton`}
                                                                                        />
                                                                                    )}

                                                                                    {values['childrenAge'].length >
                                                                                        1 && (
                                                                                        <AmountButton
                                                                                            text={
                                                                                                cms.common
                                                                                                    .removeItemButton
                                                                                            }
                                                                                            actionType="decrease"
                                                                                            onClick={() => {
                                                                                                // remove s indexem, protoze strictMode vola arrayHelpers.pop(); dvakrat
                                                                                                arrayHelpers.remove(
                                                                                                    values[
                                                                                                        'childrenAge'
                                                                                                    ].length - 1
                                                                                                );
                                                                                                // Po odstranění prvku zavoláme validaci znovu pomocí useEffect,
                                                                                                // neboť docházelo k validaci dříve, než byl prvek odstraněn a vladice tak byla chybná.
                                                                                                if (submittedPopup) {
                                                                                                    setForceValidation(
                                                                                                        true
                                                                                                    );
                                                                                                }
                                                                                            }}
                                                                                            data-test={`removeChildButton`}
                                                                                        />
                                                                                    )}
                                                                                </>
                                                                            )}
                                                                        </div>
                                                                    );
                                                                }
                                                            )}
                                                        </div>
                                                    )}
                                                />
                                            </Grid>
                                        </div>
                                    </>
                                )}

                                <div className="w-100">
                                    <Label htmlFor="mortgage" data-test="hasMortgage">
                                        Máte hypotéku?
                                    </Label>
                                    <Grid cols="auto">
                                        <ToggleListWrapper>
                                            <div className="toggle-list">
                                                <ToggleField
                                                    name="mortgage"
                                                    id="mortgage-yes"
                                                    variant="radio"
                                                    value={config.QUESTIONNAIRE.ANSWER_YES}
                                                    error={!!errors.mortgage}
                                                    optional
                                                    data-test="hasMortgage[Yes]"
                                                />
                                                <ToggleField
                                                    name="mortgage"
                                                    id="mortgage-no"
                                                    variant="radio"
                                                    value={config.QUESTIONNAIRE.ANSWER_NO}
                                                    error={!!errors.mortgage}
                                                    optional
                                                    data-test="hasMortgage[No]"
                                                />
                                            </div>
                                            {errors.mortgage && (
                                                <FormItemError text={errors.mortgage} data-test="hasMortgageError" />
                                            )}
                                        </ToggleListWrapper>
                                    </Grid>
                                </div>

                                {values && values['mortgage'] === config.QUESTIONNAIRE.ANSWER_YES && (
                                    <div className={cn(styles.fullsize, 'w-100')}>
                                        <Grid cols={2} className={styles.mortgageGrid}>
                                            <div>
                                                <Label
                                                    htmlFor="mortgageAlone"
                                                    description="Tedy nemáte spoludlužníka - např. partnera"
                                                    children={'<b>Platíte hypotéku sám?</b>'}
                                                    data-test="mortgageAloneLabel"
                                                />
                                                <Grid cols="auto">
                                                    <ToggleListWrapper>
                                                        <div className="toggle-list">
                                                            <ToggleField
                                                                name="mortgageAlone"
                                                                id="mortgageAlone-yes"
                                                                variant="radio"
                                                                value={config.QUESTIONNAIRE.ANSWER_YES}
                                                                error={errors.mortgageAlone}
                                                                data-test="mortgageAlone[Yes]"
                                                            />
                                                            <ToggleField
                                                                name="mortgageAlone"
                                                                id="mortgageAlone-no"
                                                                variant="radio"
                                                                value={config.QUESTIONNAIRE.ANSWER_NO}
                                                                error={errors.mortgageAlone}
                                                                data-test="mortgageAlone[No]"
                                                            />
                                                        </div>
                                                        {errors.mortgageAlone && (
                                                            <FormItemError
                                                                text={errors.mortgageAlone}
                                                                data-test="mortgageAloneError"
                                                            />
                                                        )}
                                                    </ToggleListWrapper>
                                                </Grid>
                                            </div>

                                            <div>
                                                <TextField
                                                    name="mortgageInterest"
                                                    variant="number"
                                                    label={[
                                                        '<b>Jaký máte u hypotéky úrok?</b>',
                                                        'Stačí zhruba na celá procenta',
                                                    ]}
                                                    suffix="%"
                                                    className="field-small"
                                                    max={99}
                                                    error={errors.mortgageInterest}
                                                    allowFieldValidation={false}
                                                    data-test="mortgageInterest"
                                                />
                                                {errors.mortgageInterest && (
                                                    <FormItemError
                                                        text={errors.mortgageInterest}
                                                        data-test="mortgageInterestError"
                                                    />
                                                )}
                                            </div>

                                            <div>
                                                <TextField
                                                    name="mortgageAmount"
                                                    variant="price"
                                                    label={[
                                                        '<b>Jaká je nesplacená výše hypotéky?</b>',
                                                        'Stačí zaokrouhleně',
                                                    ]}
                                                    suffix={config.CURRENCIES.CZK}
                                                    className="field-medium"
                                                    min={1}
                                                    max={config.QUESTIONNAIRE.MORTGAGE_MAXAMOUNT}
                                                    error={errors.mortgageAmount}
                                                    data-test="mortgageAmount"
                                                />
                                                {errors.mortgageAmount && (
                                                    <FormItemError
                                                        text={errors.mortgageAmount}
                                                        data-test="mortgageAmountError"
                                                    />
                                                )}
                                            </div>

                                            <div>
                                                <TextField
                                                    name="mortgagePeriod"
                                                    variant="number"
                                                    label={[
                                                        '<b>Jak dlouho ještě budete splácet?</b>',
                                                        'Stačí zaokrouhleně na celé roky',
                                                    ]}
                                                    suffix={formatYears(values['mortgagePeriod'])}
                                                    className="field-small"
                                                    max={99}
                                                    error={errors.mortgagePeriod}
                                                    allowFieldValidation={false}
                                                    data-test="mortgagePeriod"
                                                />
                                                {errors.mortgagePeriod && (
                                                    <FormItemError
                                                        text={errors.mortgagePeriod}
                                                        data-test="mortgagePeriodError"
                                                    />
                                                )}
                                            </div>
                                        </Grid>
                                    </div>
                                )}

                                <Grid cols="auto" className={styles.buttonsWrapper}>
                                    <Button
                                        variant="primary"
                                        onClick={() => {
                                            unlockScroll();
                                            triggerGtmEvent('suggest_go');
                                            handleSubmit();
                                        }}
                                        data-test="saveButton"
                                    >
                                        {cms.common.saveButton}
                                    </Button>
                                </Grid>
                            </Form>
                        )}
                    </Formik>
                )}
            </div>
        </div>
    );
};

export default MortgagePopup;
